import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone, Clock, Loader2 } from "lucide-react";
import { submitContact, subscribeNewsletter } from "../../lib/api";

const INQUIRY_TYPES = [
  "Sourcing from China",
  "Customs clearance",
  "Door-to-door shipping",
  "Yiwu market visit",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    inquiry_type: INQUIRY_TYPES[0],
    message: "",
    subscribe: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [subEmail, setSubEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Request received. We'll reply within one business day.");
      setForm({
        name: "",
        email: "",
        company: "",
        inquiry_type: INQUIRY_TYPES[0],
        message: "",
        subscribe: true,
      });
    } catch (err) {
      toast.error("Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) {
      toast.error("Add your email to subscribe.");
      return;
    }
    setSubscribing(true);
    try {
      await subscribeNewsletter(subEmail);
      toast.success("You're in. Check your inbox for 10% off your first contract.");
      setSubEmail("");
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-14 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — lead + info */}
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              05 — Get in touch
            </p>
            <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Start a shipment.<br />
              Get a real answer.
            </h2>
            <p className="mt-6 text-ink-500 text-base md:text-lg leading-relaxed max-w-md">
              Tell us what you need sourced or moved. We'll come back with a
              quote, a route and a timeline — no boilerplate.
            </p>

            <div className="mt-12 space-y-5">
              <InfoRow
                icon={MapPin}
                label="Yiwu Headquarters"
                value="402, Wealth Mansion, Yiwu, Zhejiang, China"
              />
              <InfoRow
                icon={Phone}
                label="Direct Line"
                value="+86 183 6797 8727"
                href="tel:008618367978727"
              />
              <InfoRow
                icon={Mail}
                label="Email"
                value="info@emporiaworld.co"
                href="mailto:info@emporiaworld.co"
              />
              <InfoRow
                icon={Clock}
                label="Hours (CST)"
                value="Mon–Sat · 09:00 – 17:00 · Closed Sun"
              />
            </div>

            {/* Newsletter card */}
            <div className="mt-12 bg-ink text-white p-7 md:p-8 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-signal/20 blur-3xl" aria-hidden />
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-4">
                Newsletter
              </p>
              <h3 className="font-heading font-bold text-2xl md:text-[26px] leading-tight tracking-tight">
                Get 10% off your first sourcing contract.
              </h3>
              <p className="mt-3 text-white/65 text-sm">
                Monthly trade briefs. No spam, unsubscribe anytime.
              </p>
              <form onSubmit={handleSubscribe} className="mt-5 flex gap-0">
                <input
                  type="email"
                  required
                  data-testid="newsletter-email-input"
                  value={subEmail}
                  onChange={(e) => setSubEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-white/5 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-signal text-sm"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-signup-button"
                  className="bg-signal hover:bg-signal-600 text-white px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-70"
                >
                  {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign up"}
                </button>
              </form>
            </div>
          </div>

          {/* Right — form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-ink-50 border border-ink-100 p-7 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" htmlFor="f-name">
                <input
                  id="f-name"
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="input"
                  placeholder="Your name"
                />
              </Field>
              <Field label="Company" htmlFor="f-company">
                <input
                  id="f-company"
                  data-testid="contact-company-input"
                  type="text"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="input"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Email" htmlFor="f-email">
                <input
                  id="f-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="input"
                  placeholder="you@company.com"
                />
              </Field>
              <Field label="Inquiry type" htmlFor="f-inquiry">
                <select
                  id="f-inquiry"
                  data-testid="contact-inquiry-select"
                  value={form.inquiry_type}
                  onChange={(e) => update("inquiry_type", e.target.value)}
                  className="input bg-white"
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Tell us about your shipment or sourcing need" htmlFor="f-message" className="mt-5">
              <textarea
                id="f-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="input resize-none"
                placeholder="Products, volume, origin/destination, preferred timeline…"
              />
            </Field>

            <label className="mt-5 flex items-start gap-3 text-sm text-ink-700 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="contact-subscribe-checkbox"
                checked={form.subscribe}
                onChange={(e) => update("subscribe", e.target.checked)}
                className="mt-1"
              />
              <span>
                Subscribe me to Emporia's monthly trade brief and claim 10% off my first contract.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-form-submit"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-ink-900 hover:bg-signal text-white px-8 py-4 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Send Request
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-ink-500">
              By submitting, you agree to be contacted about your inquiry.
            </p>
          </form>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #fff;
          border: 1px solid #E4E7EC;
          padding: 12px 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 15px;
          color: #101828;
          outline: none;
          border-radius: 2px;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .input::placeholder { color: #98A2B3; }
        .input:focus {
          border-color: #FF3B30;
          box-shadow: 0 0 0 3px rgba(255,59,48,0.12);
        }
      `}</style>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <span className="w-10 h-10 flex-shrink-0 border border-ink-100 flex items-center justify-center text-ink-900">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500">
          {label}
        </div>
        <div className="mt-1 text-ink-900 text-[15px] leading-snug">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:text-signal transition-colors">
      {content}
    </a>
  ) : (
    content
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <label htmlFor={htmlFor} className={`block ${className}`}>
      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
